import { useCallback, useEffect, useMemo, useState } from "react";

import { DEPRECATED_Loading } from "@ui/components/DEPRECATED_Loading";
import { Form, FormField, useForm } from "@ui/components/Form";
import { InputIcon } from "@ui/components/Inputs/components";
import { TextInput } from "@ui/components/Inputs/TextInput/TextInput";
import { Recaptchabar } from "@ui/components/Recaptchabar";
import { ResponsiveImage } from "@ui/components/ResponsiveImage";
import { useEmailPattern } from "@ui/hooks/useEmailPattern";
import { useUser } from "@ui/hooks/useUser";
import { useTranslations } from "@ui/providers/useTranslations";
import { displayToastError } from "@ui/utils/displayToastError";
import { replacePlaceholdersWithLinks } from "@ui/utils/linkReplacer/linkReplacer";
import classNames from "classnames";
import dompurify from "isomorphic-dompurify";

import { useConfig } from "@jti/app-context/useConfig";
import { MailClassicIcon } from "@jti/brand-icons/mail-classic";
import { Button } from "@jti/core-components/components/Button";
import { Checkbox } from "@jti/core-components/components/Checkbox";
import type { OtPurpose } from "@jti/fe-common";
import type { RestResponseType } from "@jti/rest-api";
import { CasPurposesApi, OmpApi, SnpApi } from "@jti/rest-api";
import type { SnpSubscribeResponse } from "@jti/rest-api-types";

import type { OtPurposeAccepted } from "../../types/FlavorFinderTypes";
import { FlavorFinderBackButton } from "../Buttons/FlavorFinderButtons";
import { FlavorFinderHeader } from "../Header/FlavorFinderHeader";
import { useFlavorFinderContext } from "../Provider/FlavorFinderContextProvider";

import type { SubscriptionFormValuesType } from "./types";
import { useDataPreparation } from "./useDataPreparation";

import "./FlavorFinderConsent.global.scss";
import styles from "./FlavorFinderConsent.module.scss";

const { sanitize } = dompurify;

const defaultValues = {
  email: "",
  consent_purposes: {},
};

export const FlavorFinderConsent = () => {
  const { dictionary } = useTranslations();
  const { flavorFinderData, handlePreviousStepClick, setCurrentView } = useFlavorFinderContext();
  const { user, isUserLoggedIn, isCheckingUser } = useUser();
  const config = useConfig();
  const emailPattern = useEmailPattern();
  const form = useForm<SubscriptionFormValuesType>({ defaultValues, shouldUnregister: false, mode: "onChange" });
  const { prepareOmpPayload, prepareSnpPayload } = useDataPreparation();

  const [isPurposesLoading, setIsPurposesLoading] = useState(true);
  const [isSubscribedInProgress, setIsSubscribedInProgress] = useState(false);
  const [consents, setConsents] = useState<OtPurposeAccepted[] | OtPurpose[]>([]);
  const [isUserConsentAlready, setIsUserConsentAlready] = useState(false);

  const { reset, formState, getValues, trigger } = form;

  const snp = useMemo(() => SnpApi.init(), []);
  const cas = useMemo(() => CasPurposesApi.init(), []);

  const {
    title,
    indicatorOverrideLabel,
    descriptionLoggedIn,
    descriptionNotLoggedIn,
    emailFieldLabel,
    emailFieldValidationErrorMessage,
    sendResultsCTALabel,
    emailFieldPlaceholder,
    backgroundImage,
    mediumBackgroundImage,
    smallBackgroundImage,
    disclaimerLoggedInConsent = "",
    disclaimerLoggedInNoConsent = "",
    disclaimerNotLoggedIn = "",
    disableSendingMailResults = false,
    isConsentFormDisabled = false,
    descriptionNotLoggedInWithoutConsent = "",
    skipAndShowResultsCtaLabelForConsentFormDisabled = "",
  } = flavorFinderData.consentPolicy;

  const isButtonDisabled = !formState.isValid;

  const leaveStep = () => {
    reset();
    setCurrentView("results");
  };

  const subscribeToServices = async (sendSnp = true) => {
    const promises: Promise<RestResponseType | SnpSubscribeResponse>[] = [
      OmpApi.init().sendMessage(prepareOmpPayload(getValues(), disableSendingMailResults), "DEFAULT"),
    ];

    if (sendSnp) {
      promises.push(snp.subscribe(prepareSnpPayload(getValues())));
    }

    const results = await Promise.allSettled(promises);

    results.forEach((result, index) => {
      const serviceName = index === 0 ? "OMP" : "SNP";

      if (result.status === "fulfilled") {
        console.log(`${serviceName} API call succeeded`);
      } else {
        console.error(`${serviceName} API call failed:`, result.reason);
        displayToastError(result.reason, undefined, dictionary);
      }
    });

    leaveStep();
  };

  const onSubscriptionSubmitHandle = async () => {
    await subscribeToServices();
  };

  const onNextStepHandle = async () => {
    if (isUserLoggedIn) {
      try {
        setIsSubscribedInProgress(true);
        await subscribeToServices(false);
      } catch (e) {
        console.error(e);
        displayToastError(e, undefined, dictionary);
      } finally {
        setIsSubscribedInProgress(false);
        leaveStep();
      }
    }
    leaveStep();
  };

  const fetchPurposes = useCallback(
    async (isLoggedIn: boolean | null) => {
      if (isLoggedIn) {
        return cas.getPurposesForUserByStep("quiz", false);
      }
      if (isConsentFormDisabled) {
        return [];
      }
      return cas.getPurposesByStep("quiz");
    },
    [cas, isConsentFormDisabled]
  );

  const prepareFormData = useCallback(
    (response: OtPurposeAccepted[] | OtPurpose[], isLoggedIn: boolean | null, email?: string) => {
      const baseData = { email: email || "" };

      if (isLoggedIn === true) {
        const purposes = (response as OtPurposeAccepted[]).reduce(
          (acc, item) => ({ ...acc, [item.id]: item.accepted || false }),
          {}
        );
        return { ...baseData, consent_purposes: purposes };
      }

      return baseData;
    },
    []
  );

  useEffect(() => {
    if (consents.length || isCheckingUser) {
      return;
    }

    setIsPurposesLoading(true);
    fetchPurposes(isUserLoggedIn)
      .then((response) => {
        setConsents(response);

        if (isUserLoggedIn === true) {
          setIsUserConsentAlready((response as OtPurposeAccepted[]).every((consent) => consent.accepted));
        }

        reset(prepareFormData(response, isUserLoggedIn, user?.email));
      })
      .catch((e) => {
        console.error(e);
        displayToastError(e, undefined, dictionary);
      })
      .finally(() => {
        setIsPurposesLoading(false);
        trigger();
      });
  }, [consents.length, reset, user?.email, fetchPurposes, isUserLoggedIn, prepareFormData, trigger, isCheckingUser]);

  const description = isUserLoggedIn ? descriptionLoggedIn : descriptionNotLoggedIn;

  const isFormShown = isUserLoggedIn ? !isUserConsentAlready : !isConsentFormDisabled;

  const consentIntroVisibleForRole = isUserLoggedIn || !isConsentFormDisabled;

  const showDescriptionNotLoggedInWithoutConsent = !isUserLoggedIn && isConsentFormDisabled;

  const isRecaptchaShown = config.reCaptchaEnabled && config.brandingInformationEnabled;

  let resultsLabel: string;
  if (isUserLoggedIn && isUserConsentAlready) {
    resultsLabel = flavorFinderData.showResultsLabel;
  } else if (showDescriptionNotLoggedInWithoutConsent) {
    resultsLabel = skipAndShowResultsCtaLabelForConsentFormDisabled;
  } else {
    resultsLabel = flavorFinderData.skipAndShowResultsCtaLabel;
  }

  const getDisclaimerText = () => {
    if (!isUserLoggedIn) {
      return disclaimerNotLoggedIn;
    }
    return isUserConsentAlready ? disclaimerLoggedInConsent : disclaimerLoggedInNoConsent;
  };

  const disclaimerClasses = classNames(styles.disclaimer, {
    [styles.disclaimerStandalone]: !isFormShown || !isRecaptchaShown,
  });

  return (
    <>
      <div className={styles.consentBackButtonSmallScreen}>
        <FlavorFinderBackButton onClick={handlePreviousStepClick} label={flavorFinderData.backCtaLabel} />
      </div>
      <div className={styles.consentWrapper}>
        <div className={styles.consent} data-testid="flavorFinderConsent">
          <FlavorFinderHeader title={title} infoText={indicatorOverrideLabel}>
            <div className={styles.consentBackButtonBigScreen}>
              <FlavorFinderBackButton onClick={handlePreviousStepClick} label={flavorFinderData.backCtaLabel} />
            </div>
          </FlavorFinderHeader>
          <div className={styles.content}>
            {isPurposesLoading && (
              <div className={styles.consentLoading} data-testid="flavorFinderConsentLoading">
                <DEPRECATED_Loading />
              </div>
            )}
            {!isPurposesLoading && (
              <>
                <ResponsiveImage
                  largeSrc={backgroundImage ?? ""}
                  mediumSrc={mediumBackgroundImage}
                  smallSrc={smallBackgroundImage}
                />
                <div className={`${styles.column} ${styles.columnBgColor}`}>
                  {description && consentIntroVisibleForRole && (
                    <p
                      className={styles.description}
                      data-testid="flavorFinderConsentDescription"
                      dangerouslySetInnerHTML={{
                        __html: sanitize(description),
                      }}
                    ></p>
                  )}
                  {descriptionNotLoggedInWithoutConsent && showDescriptionNotLoggedInWithoutConsent && (
                    <p
                      className={styles.description}
                      dangerouslySetInnerHTML={{
                        __html: sanitize(descriptionNotLoggedInWithoutConsent),
                      }}
                    ></p>
                  )}
                  {isFormShown && (
                    <Form<SubscriptionFormValuesType>
                      key="consentForm"
                      form={form}
                      onSubmit={onSubscriptionSubmitHandle}
                      dataTestId="flavorFinderConsentForm"
                    >
                      <div className={styles.emailInputContainer}>
                        <FormField
                          id="email"
                          name="email"
                          dataTestId="flavorFinderFormEmail"
                          label={emailFieldLabel}
                          showCustomError
                          validation={{
                            required: emailFieldValidationErrorMessage,
                            pattern: emailPattern,
                          }}
                          render={({ field, fieldState }) => (
                            <TextInput
                              {...field}
                              id="email"
                              type="email"
                              elemBefore={<InputIcon icon={MailClassicIcon} />}
                              placeholder={emailFieldPlaceholder}
                              autoComplete="off"
                              disabled={isUserLoggedIn}
                              data-testid="flavorFinderConsentFormEmailInput"
                              isError={fieldState.invalid}
                              errorText={fieldState.error?.message || ""}
                              containerClassName="light-theme"
                            />
                          )}
                        />
                      </div>
                      {consents.map((consent) => (
                        <div className={styles.consentFormWrapper} key={consent.id}>
                          <FormField
                            name={`consent_purposes.${consent.id}`}
                            className={"light-theme"}
                            defaultValue={("accepted" in consent && consent.accepted) || false}
                            validation={{ required: consent.mandatory }}
                            dataTestId="flavorFinderFormConsentCheckboxes"
                            render={({ field }) => (
                              <div className={styles.consentCheckbox}>
                                <Checkbox
                                  id={`consent_purposes.${consent.id}`}
                                  data-testid={`flavorFinderConsentFormConsentCheckbox.${consent.id}`}
                                  checked={field.value}
                                  onCheckedChange={(checked) => {
                                    field.onChange(checked);
                                  }}
                                  label={
                                    <div className={styles.consentLabel}>
                                      <p
                                        className={styles.consentDescription}
                                        dangerouslySetInnerHTML={{
                                          __html: sanitize(
                                            replacePlaceholdersWithLinks(
                                              `${consent.description}`,
                                              config.ONE_TRUST_DYNAMIC_LINKS ?? []
                                            )
                                          ),
                                        }}
                                      ></p>
                                    </div>
                                  }
                                  required={consent.mandatory}
                                />
                              </div>
                            )}
                          />
                        </div>
                      ))}
                      <Button
                        type="submit"
                        className={classNames("light-theme", styles.submitButton)}
                        variant="primary"
                        pending={formState.isSubmitting}
                        disabled={isButtonDisabled}
                        data-testid="flavorFinderConsentFormSendButton"
                      >
                        {sendResultsCTALabel}
                      </Button>
                      {isRecaptchaShown && <Recaptchabar className="light-theme" />}
                    </Form>
                  )}
                  {getDisclaimerText() && consentIntroVisibleForRole && (
                    <div
                      className={disclaimerClasses}
                      dangerouslySetInnerHTML={{
                        __html: sanitize(getDisclaimerText()),
                      }}
                    />
                  )}
                </div>
              </>
            )}
          </div>
          {!isPurposesLoading && (
            <div className="flavorfinder__footer">
              <Button
                variant="text-muted"
                className={classNames("light-theme", styles.consentNextButton, {
                  [styles.consentNextButtonPending]: isSubscribedInProgress,
                })}
                onClick={onNextStepHandle}
                disabled={isSubscribedInProgress}
              >
                {resultsLabel}
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
